import { Button, Card, Checkbox, DatePicker, Divider, Form, Grid, Input, InputNumber, Modal, Popover, Radio, Select, Slider, Space, Tag, Tooltip, message, Transfer, Table } from "antd";
import dayjs from "dayjs";
import { EcrfForm, EcrfSection, FieldTypeEnum, FormField, SaveFormDataVM, FormData, Inquiry } from "../models/models";
import { FormInstance, useForm, useWatch } from "antd/es/form/Form";
import { Rule } from "antd/es/form";
import { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../App";
import { resolveInquiry, saveFormValues } from "../models/serverApi";
import { CalculatorFilled, QuestionCircleOutlined, MessageOutlined, StopOutlined, HourglassOutlined, ExclamationCircleOutlined, MinusOutlined, VerticalAlignBottomOutlined } from "@ant-design/icons";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { bmiCalculation, egfrCalculation } from "../Calculations";
import { InquiryModal } from "./InquiryModal";

// A value is "empty" when there is nothing meaningful to store. Note 0 and false
// are NOT empty - only null/undefined, blank strings and empty arrays are.
const isEmptyValue = (v: any): boolean => {
    if (v === null || v === undefined) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (Array.isArray(v)) return v.length === 0;
    if (dayjs.isDayjs(v)) return !v.isValid();
    return false;
};

// FormFields has a composite primary key (FormId, Id): a field Id is only unique
// within its form, so Id=1 exists in both form 1002 and form 2002. section.form
// .formFields is already scoped to one form, so a plain id match is correct here -
// this helper just makes that scoping explicit and keeps the two call sites
// (reload and save) consistent.
const findField = (section: EcrfSection | null | undefined, fieldId: number): FormField | undefined => {
    const fields = section?.form?.formFields;
    if (!fields) return undefined;
    const formId = section?.form?.id;
    return fields.find(ff => ff.id == fieldId && (ff as any).formId == formId)
        ?? fields.find(ff => ff.id == fieldId);
};

// Reads CategoryTargetFieldId out of a field's ExtraStyle - the id of the field
// that receives the derived categories of this field's selection.
const getCategoryTargetFieldId = (formField: FormField): number | null => {
    if (!formField?.extraStyle) return null;
    try {
        const xt = JSON.parse(formField.extraStyle);
        const raw = xt?.CategoryTargetFieldId;
        if (raw === null || raw === undefined) return null;
        const id = parseInt(`${raw}`, 10);
        return Number.isNaN(id) ? null : id;
    }
    catch {
        return null;
    }
};

// Parses a field's ListValues into option objects. Mirrors the option parsing used
// by the MultiSelect/TransferTable renderers.
const parseOptions = (listValues: string | null): any[] => {
    if (!listValues) return [];
    const lv = listValues.trim();
    if (lv.startsWith('[') || lv.startsWith('{')) {
        try { return JSON.parse(lv); } catch { return []; }
    }
    return [];
};

// Normalises a field's ListValues into {value,label} pairs, accepting either
// format: a JSON array of option objects, or a plain comma-separated list.
//
// JSON lets the stored value differ from the displayed label - needed wherever a
// codelist defines codes (e.g. sex 1=Άνδρας, 2=Γυναίκα) so the code is what gets
// persisted. CSV keeps simple fields terse, storing the label as the value.
// Falls back to CSV if the JSON is malformed, so a bad definition degrades to
// something usable rather than rendering nothing.
const parseOptionPairs = (listValues: string | null): { value: string, label: string }[] => {
    if (!listValues) return [];

    const asCsv = () => listValues
        .split(',')
        .map(o => o.trim())
        .filter(o => o !== '')
        .map(o => ({ value: o, label: o }));

    const lv = listValues.trim();
    if (!lv.startsWith('[') && !lv.startsWith('{')) return asCsv();

    let parsed: any;
    try { parsed = JSON.parse(lv); }
    catch { return asCsv(); }

    if (!Array.isArray(parsed)) return asCsv();

    return parsed
        .map(opt => {
            // Scalars inside a JSON array behave like a CSV entry.
            if (typeof opt !== 'object' || opt === null) {
                const v = `${opt}`.trim();
                return v === '' ? null : { value: v, label: v };
            }
            const value = opt.value ?? opt.id ?? opt.key;
            if (value === undefined || value === null || `${value}`.trim() === '') return null;
            const label = opt.label ?? opt.name ?? opt.value;
            return { value: `${value}`, label: `${label ?? value}` };
        })
        .filter((o): o is { value: string, label: string } => o !== null);
};

// Client-side mirror of the server's ApplyDerivedCategories: maps selected values to
// their distinct categories, ordered by first appearance in the option list.
//
// This exists purely so the display updates live as the user selects. The server
// recomputes the same thing on save and its result is what gets stored - if the two
// ever disagree, the server wins. Keep this in sync with FormsController
// .MapValuesToCategories.
const deriveCategories = (selected: any, listValues: string | null): string[] => {
    if (!Array.isArray(selected) || selected.length === 0) return [];

    const options = parseOptions(listValues);
    if (options.length === 0) return [];

    const selectedSet = new Set(selected.map(v => `${v}`));
    const categories: string[] = [];

    options.forEach(opt => {
        if (typeof opt !== 'object' || opt === null) return;
        const value = opt.value ?? opt.id ?? opt.key;
        const category = opt.category;
        if (value === undefined || value === null) return;
        if (!category || `${category}`.trim() === '') return;
        if (selectedSet.has(`${value}`) && !categories.includes(category)) {
            categories.push(category);
        }
    });

    return categories;
};

// Reads a numeric-formula spec out of a field's ExtraStyle. A field carrying one is
// computed from other fields rather than entered by the user.
const getFormulaSpec = (formField: FormField): { formula: string, sources: number[], decimals?: number, months?: number, codes?: string[] } | null => {
    if (!formField?.extraStyle) return null;
    try {
        const xt = JSON.parse(formField.extraStyle);
        if (!xt?.Formula || !Array.isArray(xt?.Sources)) return null;
        const sources = xt.Sources
            .map((s: any) => parseInt(`${s}`, 10))
            .filter((n: number) => !Number.isNaN(n));
        if (sources.length === 0) return null;
        return {
            formula: `${xt.Formula}`,
            sources,
            decimals: typeof xt.Decimals === 'number' ? xt.Decimals : undefined,
            months: typeof xt.Months === 'number' ? xt.Months : undefined,
            codes: Array.isArray(xt.Codes) ? xt.Codes.map((c: any) => `${c}`) : undefined,
        };
    }
    catch {
        return null;
    }
};

const toNumber = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(`${v}`.trim());
    return Number.isFinite(n) ? n : null;
};

// Normalises a date operand to a UTC-midnight timestamp. Accepts a live dayjs from
// the form, a Date, or a stored string (ISO timestamp, or plain yyyy-MM-dd from a
// month-precision field). Anchoring to UTC midnight keeps a difference in days
// independent of the browser timezone and immune to DST shifts between operands.
const toDate = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;

    const d = dayjs.isDayjs(v) ? v : dayjs(v);
    if (!d.isValid()) return null;

    return Date.UTC(d.year(), d.month(), d.date());
};

// Client-side mirror of the server's EvaluateFormula. Exists only so the derived
// value updates live as the user types; the server recomputes on save and its result
// is what gets stored. Keep in sync with FormsController.EvaluateFormula - including
// the named-formula whitelist, which deliberately avoids evaluating expressions
// supplied by the form definition.
//
// Returns null when an operand is missing, so an incomplete form shows a blank rather
// than a misleading 0.
// Formulas whose result is a yesno codelist code rather than a measurement.
// Mirrors FormsController.IsFlagFormula - keep the two lists in sync.
const isFlagFormula = (formula: string): boolean =>
    formula === 'anyMissing' || formula === 'anyOlderThan' || formula === 'containsAny';

const computeFormula = (formula: string, args: any[], decimals?: number, months?: number, codes?: string[]): number | null => {
    let raw: number | null = null;

    switch (formula) {
        // BMI: weight(kg) / height(m)^2, height supplied in cm.
        case 'bmi': {
            const [w, hCm] = args.map(toNumber);
            if (w == null || hCm == null || hCm === 0) return null;
            const m = hCm / 100;
            raw = w / (m * m);
            break;
        }
        // Simple ratio, e.g. waist/hip for WHR.
        case 'ratio': {
            const [a, b] = args.map(toNumber);
            if (a == null || b == null || b === 0) return null;
            raw = a / b;
            break;
        }
        // CKD-EPI 2021 (race-free), the formula the protocol specifies.
        //
        //   eGFR = 142 × min(Scr/κ,1)^α × max(Scr/κ,1)^-1.200 × 0.9938^age
        //          × 1.012 [if female]
        //
        //   κ = 0.7 (female) / 0.9 (male)
        //   α = -0.241 (female) / -0.302 (male)
        //
        // args: [creatinine mg/dL, age years, sex code]
        //
        // NOT the 2009 equation still sitting in Calculations.tsx, which carries
        // a race coefficient and is not what this study reports. That one is
        // unused and should be removed rather than left to be wired up by
        // mistake.
        case 'ckdEpi2021': {
            const [scr, age] = [toNumber(args[0]), toNumber(args[1])];
            const sexRaw = args[2] === null || args[2] === undefined ? "" : `${args[2]}`.trim();
            if (scr == null || age == null || scr <= 0 || age <= 0 || sexRaw === "") return null;

            // Sex codes come from Section D's codelist. Anything that is not
            // recognisably female is treated as male rather than guessed at - and
            // an unknown code returns null instead of silently picking a branch,
            // because the two differ by ~15% and a wrong eGFR is a wrong CKD stage.
            const female = sexRaw === "2" || sexRaw.toLowerCase().startsWith("f") || sexRaw.startsWith("Γ") || sexRaw.startsWith("γ");
            const male = sexRaw === "1" || sexRaw.toLowerCase().startsWith("m") || sexRaw.startsWith("Α") || sexRaw.startsWith("α");
            if (!female && !male) return null;

            const k = female ? 0.7 : 0.9;
            const alpha = female ? -0.241 : -0.302;
            const ratio = scr / k;

            raw = 142
                * Math.pow(Math.min(ratio, 1), alpha)
                * Math.pow(Math.max(ratio, 1), -1.200)
                * Math.pow(0.9938, age)
                * (female ? 1.012 : 1);
            break;
        }
        // Elapsed years between two dates, ordered [later, earlier].
        case 'yearsBetween': {
            const later = toDate(args[0]);
            const earlier = toDate(args[1]);
            if (later === null || earlier === null) return null;
            // Compared as UTC midnights so the result cannot shift with the
            // browser's timezone or a DST boundary between the two dates.
            raw = (later - earlier) / (1000 * 60 * 60 * 24 * 365.25);
            break;
        }
        // 1 when any source array contains one of `codes`, else 0.
        // Section J uses this against the drug sections' DERIVED CATEGORY fields,
        // so a class name is matched rather than every molecule in the class.
        case 'containsAny': {
            if (!codes || codes.length === 0) return null;
            const selected = new Set<string>();
            args.forEach(a => {
                const arr = Array.isArray(a) ? a : (typeof a === 'string' ? parseOptions(a) : []);
                if (Array.isArray(arr)) arr.forEach((v: any) => selected.add(`${v}`.trim()));
            });
            raw = codes.some(c => selected.has(`${c}`.trim())) ? 1 : 0;
            break;
        }
        // 1 when ANY source is empty, else 0.
        case 'anyMissing': {
            if (args.length === 0) return null;
            raw = args.some(a => isEmptyValue(a)) ? 1 : 0;
            break;
        }
        // 1 when ANY date is more than `months` before the reference date.
        // Sources are ordered [reference, date1, date2, ...]. A missing date is
        // not "stale" - that is what anyMissing reports.
        case 'anyOlderThan': {
            if (args.length < 2) return null;
            const reference = toDate(args[0]);
            if (reference === null) return null;
            const ref = new Date(reference);
            const cutoff = Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth() - (months ?? 6), ref.getUTCDate());
            raw = args.slice(1)
                .map(toDate)
                .some(d => d !== null && d < cutoff) ? 1 : 0;
            break;
        }
        default:
            return null;
    }

    // Flag formulas yield a yesno code, not a measurement: no rounding.
    if (isFlagFormula(formula)) return raw;

    if (raw == null || !Number.isFinite(raw)) return null;

    const d = decimals ?? 2;
    const f = Math.pow(10, d);
    return Math.round(raw * f) / f;
};

// Read-only renderer for a formula-derived numeric value. Like
// DerivedCategoriesDisplay, it must stay the direct child of its Form.Item.
const DerivedFormulaDisplay: React.FC<{
    value?: any,
    liveValue?: number | null,
    units?: string,
    blockedBy?: string | null,
}> = ({ value, liveValue, units, blockedBy }) => {
    const shown = liveValue !== undefined ? liveValue : toNumber(value);

    // The calculation cannot run because a value it needs from ANOTHER section is
    // missing. Called out explicitly rather than shown as "waiting": the user cannot
    // fix it from this form, so they need to be told where to go.
    if (blockedBy) {
        return (
            <Tooltip title={blockedBy}>
                <span className="derived-pill derived-pill-blocked">
                    <ExclamationCircleOutlined className="derived-pill-icon" />
                    <span className="derived-pill-blocked-text">Δεν είναι δυνατός ο υπολογισμός</span>
                </span>
            </Tooltip>
        );
    }

    // Rendered as a filled pill rather than a bare dash so it reads as a computed
    // output, not an input the user is expected to fill in.
    //
    // Waiting state is an icon alone: the field is inert until its sources are
    // filled, and spelling that out took more width than the value it replaces.
    if (shown === null || shown === undefined) {
        return (
            <Tooltip title="Υπολογίζεται αυτόματα όταν συμπληρωθούν τα πεδία προέλευσης">
                <span className="derived-pill derived-pill-empty">
                    <HourglassOutlined className="derived-pill-icon" />
                </span>
            </Tooltip>
        );
    }

    return (
        <span className="derived-pill">
            <CalculatorFilled className="derived-pill-icon" />
            <span className="derived-pill-value">{shown}</span>
            {units ? <span className="derived-pill-units">{units}</span> : null}
        </span>
    );
};

// Orders fields by displayOrder, then lifts every derived-category field to sit
// immediately after the field it is derived from - regardless of its own
// displayOrder or id - so the pair always reads together on the form.
const orderFieldsWithDerived = (formFields: FormField[]): FormField[] => {
    // copy first: .sort() mutates, and this array comes from props
    const ordered = [...formFields].sort((a, b) => a.displayOrder - b.displayOrder);

    const targetIds = new Map<number, number>(); // targetFieldId -> sourceFieldId
    ordered.forEach(ff => {
        const targetId = getCategoryTargetFieldId(ff);
        if (targetId != null) targetIds.set(targetId, ff.id);
    });

    // A formula field declares its own sources (rather than a source pointing at it),
    // so anchor it after whichever source appears last on the form - that way all its
    // operands are on screen above it.
    ordered.forEach(ff => {
        const spec = getFormulaSpec(ff);
        if (!spec) return;
        const positions = spec.sources
            .map(id => ordered.findIndex(o => o.id === id))
            .filter(i => i >= 0);
        if (positions.length === 0) return;
        targetIds.set(ff.id, ordered[Math.max(...positions)].id);
    });

    if (targetIds.size === 0) return ordered;

    const byId = new Map(ordered.map(ff => [ff.id, ff]));
    const result: FormField[] = [];

    ordered.forEach(ff => {
        // derived fields are emitted next to their source, not in their own slot
        if (targetIds.has(ff.id)) return;

        result.push(ff);

        // append any derived field(s) that point at the field just emitted
        targetIds.forEach((sourceId, targetId) => {
            if (sourceId === ff.id) {
                const derived = byId.get(targetId);
                if (derived) result.push(derived);
            }
        });
    });

    return result;
};

// Splits the ordered fields into titled groups. A field starting a new group
// declares {"Group":"Ανθρωπομετρικά"} in its ExtraStyle; fields before the first
// such marker form an untitled leading group, so a form that declares no groups
// renders exactly as before.
const groupFields = (formFields: FormField[]): { title: string | null, fields: FormField[] }[] => {
    const groups: { title: string | null, fields: FormField[] }[] = [];

    formFields.forEach(ff => {
        let groupTitle: string | null = null;
        if (ff.extraStyle) {
            try {
                const xt = JSON.parse(ff.extraStyle);
                const raw = xt?.Group;
                if (raw !== null && raw !== undefined && `${raw}`.trim() !== '') {
                    groupTitle = `${raw}`.trim();
                }
            }
            catch {
                // free-form ExtraStyle - no group marker
            }
        }

        if (groupTitle !== null || groups.length === 0) {
            groups.push({ title: groupTitle, fields: [ff] });
            return;
        }

        groups[groups.length - 1].fields.push(ff);
    });

    return groups;
};

// DatePicker that tolerates a non-dayjs value.
//
// antd assumes a picker's value is always a dayjs or nullish: its internal
// getUDayjs() returns anything else unchanged and then calls .isValid() on it,
// which throws and takes down the whole page rather than degrading one field.
// A raw string can reach the store from several directions - a value saved before a
// field changed type, a mirror populated from another section, or a stale entry left
// by a previous section - so the coercion belongs here, at the single boundary where
// a value is handed to antd, rather than at each of the places that can write one.
//
// Must stay the Form.Item's direct child: it receives value/onChange by injection.
const SafeDatePicker: React.FC<any> = ({ value, onChange, ...rest }) => {
    let safeValue: dayjs.Dayjs | null = null;
    if (value !== null && value !== undefined && value !== '') {
        const d = dayjs.isDayjs(value) ? value : dayjs(value);
        safeValue = d.isValid() ? d : null;
    }

    return <DatePicker {...rest} value={safeValue} onChange={onChange} />;
};

// Read-only renderer for a derived yes/no flag. Coloured when set, because these
// flags mark a data-quality problem the user should notice (stale or missing labs)
// rather than a neutral computed value.
const DerivedFlagDisplay: React.FC<{ label?: string, isSet: boolean }> = ({ label, isSet }) => {
    if (label === undefined) {
        return (
            <Tooltip title="Υπολογίζεται αυτόματα όταν συμπληρωθούν τα πεδία προέλευσης">
                <span className="derived-pill derived-pill-empty">
                    <HourglassOutlined className="derived-pill-icon" />
                </span>
            </Tooltip>
        );
    }

    return (
        <span className={`derived-pill${isSet ? ' derived-pill-blocked' : ''}`}>
            {isSet
                ? <ExclamationCircleOutlined className="derived-pill-icon" />
                : <CalculatorFilled className="derived-pill-icon" />}
            <span className={isSet ? 'derived-pill-blocked-text' : 'derived-pill-value'}>{label}</span>
        </span>
    );
};

// A field declaring {"DateFillSource":true} offers to copy its own date into every
// other empty date field on the section. Section I records 24 lab dates that are
// usually the same draw, so entering them one by one is the bulk of the work there.
const isDateFillSource = (formField: FormField | null | undefined): boolean => {
    if (!formField?.extraStyle) return false;
    try {
        return JSON.parse(formField.extraStyle)?.DateFillSource === true;
    }
    catch {
        return false;
    }
};

// The date fields a fill action may write to: every DateTime field on the section
// except the source itself and any mirror (server-owned, not user-editable).
const getDateFillTargets = (section: EcrfSection | null | undefined, sourceId: number): FormField[] =>
    (section?.form?.formFields ?? []).filter(ff =>
        ff.fieldType === FieldTypeEnum.DateTime
        && ff.id !== sourceId
        && !isMirrorField(ff));

/**
 * TransferTable fields whose left pane has rows ticked but not moved across.
 *
 * Module-level rather than component state or a ref: the Transfer is rendered by
 * FormFieldRender while the save runs in FormView, two separate components, and
 * threading a ref through both call sites would touch far more code than this
 * warrant. Only one section's form is mounted at a time, and the map is cleared
 * on mount, so there is no cross-form leakage.
 *
 * Written during a render prop, so it must not be state - that would re-render
 * on every tick. Nothing renders from it; it is read once, at submit.
 */
const pendingTransferRef = { current: new Map<number, { label: string; count: number }>() };

// A field declaring {"PairWith":N} renders N's control on its OWN row, sharing
// its label. Used for a measurement and its measurement date: the two are one
// observation, and giving the date its own row with a label that just restates
// the one above it doubled the height of Section I for no information.
const getPairWithId = (formField: FormField | null | undefined): number | null => {
    if (!formField?.extraStyle) return null;
    try {
        const raw = JSON.parse(formField.extraStyle)?.PairWith;
        if (raw === null || raw === undefined) return null;
        const id = parseInt(`${raw}`, 10);
        return Number.isNaN(id) ? null : id;
    }
    catch {
        return null;
    }
};

// Ids that are rendered as somebody else's partner, so the render loop can skip
// their standalone row.
const getPairedAwayIds = (formFields: FormField[]): Set<number> => {
    const out = new Set<number>();
    formFields.forEach(ff => {
        const id = getPairWithId(ff);
        if (id != null) out.add(id);
    });
    return out;
};

// A mirror field is a read-only copy of a field on another section, declared as
// {"MirrorOf":{"formId":N,"fieldId":M}}. The server populates it on load and re-reads
// it on save, so the investigator never re-enters a value they already gave.
const isMirrorField = (formField: FormField | null | undefined): boolean => {
    if (!formField?.extraStyle) return false;
    try {
        return !!JSON.parse(formField.extraStyle)?.MirrorOf;
    }
    catch {
        return false;
    }
};

// Reads a field's {"Conditional":{"fieldId":N,"value":"x"}} marker, if any.
const getConditional = (formField: FormField | null | undefined): { fieldId: number, value: string | string[] } | null => {
    if (!formField?.extraStyle) return null;
    try {
        const c = JSON.parse(formField.extraStyle)?.Conditional;
        if (!c || c.fieldId === undefined || c.fieldId === null) return null;
        const fieldId = parseInt(`${c.fieldId}`, 10);
        if (Number.isNaN(fieldId)) return null;

        // An array value must survive as an array. This used to coerce with
        // `${c.value ?? ""}`, which turned ["current","former"] into the single
        // string "current,former" - so the multi-state check in
        // isConditionallyVisible could never match and the smoking sub-fields
        // stayed hidden whatever the investigator picked.
        const value = Array.isArray(c.value)
            ? c.value.map((v: any) => `${v ?? ""}`)
            : `${c.value ?? ""}`;

        return { fieldId, value };
    }
    catch {
        return null;
    }
};

// Whether a conditional field is currently shown, given the form's live values.
// Used by both the render-time visibility effect and formSubmit, so what the user
// sees and what gets saved can never disagree.
const isConditionallyVisible = (formField: FormField, readValue: (key: string) => any): boolean => {
    const conditional = getConditional(formField);
    if (!conditional) return true;

    const norm = (x: any) => (x === null || x === undefined) ? "" : `${x}`.trim();
    const actual = norm(readValue(`field-${conditional.fieldId}`));

    // `value` may be a single code or a list of them:
    //   {"Conditional":{"fieldId":14,"value":"former"}}
    //   {"Conditional":{"fieldId":14,"value":["current","former"]}}
    //
    // The list form exists for controls with more than two states - the smoking
    // sub-fields apply to current AND former smokers but not to never-smokers.
    // Without this an array would stringify to "current,former" and match
    // nothing, so the dependent fields would silently never appear.
    if (Array.isArray(conditional.value)) {
        return conditional.value.some((v: any) => norm(v) === actual);
    }

    return actual === norm(conditional.value);
};

// Month-precision date fields ({"DateTimeFormat":"month"}) record only a month, but
// the column still holds a full date. The protocol pins the day to the 15th
// (ΕΕΕΕ-ΜΜ-15) - mid-month, so a date-difference is never biased by more than ~2
// weeks in either direction, which matters because these feed duration formulas.
const MONTH_PRECISION_DAY = 15;

const isMonthPrecision = (formField: FormField | null | undefined): boolean => {
    if (!formField?.extraStyle) return false;
    try {
        return JSON.parse(formField.extraStyle)?.DateTimeFormat === "month";
    }
    catch {
        return false;
    }
};

// {"DateTimeFormat":"month","DayOptional":true} renders a full day picker instead
// of a month picker: the investigator enters the exact day when the record shows
// one, and ticks "Άγνωστη ημέρα" when it does not.
//
// The stored shape is unchanged - still a full date, still day 15 when the day is
// unknown - so everything already in the database stays valid and the duration
// formulas keep their mid-month centring. What is new is that 15 alone no longer
// means "unknown": a real 15th and a placeholder 15th are indistinguishable in the
// date column, so the flag below records which it was.
const isDayOptional = (formField: FormField | null | undefined): boolean => {
    if (!formField?.extraStyle) return false;
    try {
        const xt = JSON.parse(formField.extraStyle);
        return xt?.DateTimeFormat === "month" && xt?.DayOptional === true;
    }
    catch {
        return false;
    }
};

// NOTE ON PRECISION
// A DayOptional date does not record whether the day is real or the mid-month
// default: 15 means "the 15th, or the day was unknown". That ambiguity is
// deliberate for now - it keeps the stored shape identical to what is already in
// the database, so nothing has to be migrated and no formula changes. If the
// analysis later needs to tell a real 15th from a placeholder, the clean way is a
// companion "day known" field per date, populated going forward; it cannot be
// reconstructed for rows captured before that exists.

// Height of the scrollable body inside each TransferTable pane.
//
// Viewport-relative rather than a fixed pixel count so the control uses a laptop
// screen well without overflowing a short one, and clamped so it can never exceed
// roughly one screen: the point is that the field, and the save button below it,
// stay reachable without scrolling the page.
//
// Both panes get the same value, so they stay level regardless of how many rows
// each currently holds.
const TRANSFER_PANE_BODY_HEIGHT = "min(48vh, 420px)";

/**
 * Documents the keyboard and paste behaviour the form supports.
 *
 * These are all invisible affordances - nothing on screen suggests that Enter
 * moves on, or that a column can be pasted - so without this panel they are
 * effectively undiscoverable and only help whoever was told about them.
 */
const ShortcutHelp: React.FC<{ hasDateFill?: boolean }> = ({ hasDateFill }) => {
    const rows: { keys: ReactNode; text: string }[] = [
        { keys: <kbd>Enter</kbd>, text: "Μετάβαση στο επόμενο πεδίο" },
        { keys: <><kbd>Tab</kbd> / <kbd>Shift</kbd>+<kbd>Tab</kbd></>, text: "Επόμενο / προηγούμενο πεδίο" },
        { keys: <><kbd>Ctrl</kbd>+<kbd>Enter</kbd></>, text: "Αποθήκευση της ενότητας" },
    ];

    return (
        <div className="ecrf-help">
            <table className="ecrf-help-table">
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td className="ecrf-help-keys">{r.keys}</td>
                            <td>{r.text}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="ecrf-help-section">
                <div className="ecrf-help-title">Επικόλληση στήλης τιμών</div>
                <p>
                    Αντιγράψτε μια στήλη τιμών (από αναφορά εργαστηρίου ή Excel) και
                    επικολλήστε την στο <b>πρώτο</b> πεδίο. Συμπληρώνονται αυτόματα και
                    τα επόμενα πεδία, ένα ανά γραμμή.
                </p>
                <ul>
                    <li>Οι κενές γραμμές αφήνουν το αντίστοιχο πεδίο κενό.</li>
                    <li>Γίνονται δεκτά τα δεκαδικά με κόμμα (7,8) και οι μονάδες (7.8 %).</li>
                    <li>Μετά την επικόλληση εμφανίζεται επιλογή <b>Αναίρεση</b>.</li>
                </ul>
            </div>

            <div className="ecrf-help-section">
                <div className="ecrf-help-title">Πεδία χωρίς τιμή</div>
                <p>
                    Το εικονίδιο <StopOutlined /> δίπλα σε ένα πεδίο το σημειώνει ως
                    «δεν μετρήθηκε» και το αποθηκεύει κενό.
                </p>
            </div>

            {hasDateFill && (
                <div className="ecrf-help-section">
                    <div className="ecrf-help-title">Ημερομηνίες</div>
                    <p>
                        Το εικονίδιο <VerticalAlignBottomOutlined /> αντιγράφει την
                        ημερομηνία σε όλες τις <b>κενές</b> ημερομηνίες της ενότητας.
                        Όσες έχουν ήδη συμπληρωθεί δεν αλλάζουν.
                    </p>
                </div>
            )}
        </div>
    );
};

const DEFAULT_NOT_SET_LABEL = "Δεν έχει καταγραφεί";

// Read-only renderer for a server-derived value (currently the categories of a
// MultiSelect/TransferTable selection). Receives `value` from the wrapping
// Form.Item, so it must stay that Form.Item's direct child. It renders no input,
// so the user cannot edit what the server owns - but the value still round-trips
// through the form, keeping the save payload complete.
const DerivedCategoriesDisplay: React.FC<{ value?: any, liveValue?: string[] }> = ({ value, liveValue }) => {
    // liveValue tracks the source field's current selection; value is what the server
    // last stored. Prefer live so the display reacts as the user edits, and fall back
    // to the stored value when the source is not on this form.
    const source = liveValue !== undefined ? liveValue : value;
    const items: string[] = Array.isArray(source)
        ? source.filter(v => v !== null && v !== undefined && `${v}`.trim() !== '')
        : [];

    if (items.length === 0) {
        return (
            <Tooltip title="Υπολογίζεται αυτόματα όταν συμπληρωθούν τα πεδία προέλευσης">
                <span className="derived-pill derived-pill-empty">
                    <HourglassOutlined className="derived-pill-icon" />
                </span>
            </Tooltip>
        );
    }

    return (
        <span className="derived-pill">
            <CalculatorFilled className="derived-pill-icon" />
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                {items.map(c => <Tag key={c} style={{ marginInlineEnd: 0 }}>{c}</Tag>)}
            </span>
        </span>
    );
};

// Icon toggle for "value not set". Receives checked/onChange from the wrapping
// Form.Item (valuePropName="checked"), so it must forward both - and it must stay
// that Form.Item's direct child for the binding to reach it.
//
// onToggle carries the existing side effect (clear the field, revalidate) and is
// called with a synthetic event shaped like the Checkbox one it replaced, so the
// handler's `e.target.checked` guard keeps working unchanged.
const NotSetToggle: React.FC<{
    checked?: boolean,
    onChange?: (checked: boolean) => void,
    label: string,
    onToggle: (e: any) => void,
}> = ({ checked, onChange, label, onToggle }) => {
    const next = !checked;
    return (
        <Tooltip title={label}>
            <button
                type="button"
                role="switch"
                aria-checked={!!checked}
                aria-label={label}
                className={`notset-toggle${checked ? ' notset-toggle-active' : ''}`}
                onClick={() => {
                    onChange?.(next);
                    onToggle({ target: { checked: next } });
                }}
            >
                <StopOutlined />
            </button>
        </Tooltip>
    );
};

// Reads AllowNotSetLabel out of the field's ExtraStyle JSON, falling back to a
// default. Safe on malformed/absent JSON.
const getAllowNotSetLabel = (formField: FormField): string => {
    if (!formField?.extraStyle) return DEFAULT_NOT_SET_LABEL;
    try {
        const xt = JSON.parse(formField.extraStyle);
        return xt?.AllowNotSetLabel ?? xt?.AllowNotSetLabelText ?? DEFAULT_NOT_SET_LABEL;
    }
    catch {
        return DEFAULT_NOT_SET_LABEL;
    }
};

export const FormView: React.FC<{
    section: EcrfSection | null | undefined,
    disabled: boolean,
    formValues: FormData | null | undefined,
    allFormValues?: FormData[] | null,
    formSaved: () => void,
    pendingInquiries?: Inquiry[],
    highlightedFieldId?: number,
    onInquiryResolved?: (id: number) => void,
    /** Advance to the next section. When supplied, a "save and next" button is
     *  offered alongside plain save, and is called only after a successful save. */
    onSaveAndNext?: () => void,
    /** Label for that button; also used to disable it on the last section. */
    nextSectionName?: string | null,
    /** Reports whether the form holds unsaved edits, so the page can warn before
     *  navigating away. Called on every change. */
    onDirtyChange?: (dirty: boolean) => void,
}> = ({ section, disabled, formValues, allFormValues, formSaved, pendingInquiries, highlightedFieldId, onInquiryResolved, onSaveAndNext, nextSectionName, onDirtyChange }) => {
    const [context, setContext] = useContext(AppContext);
    const [antdForm] = useForm();
    // Stack label over input only on narrow screens - below md a side-by-side label
    // column leaves too little width for the control to be usable.
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [saving, setSaving] = useState(false);

    // Unsaved-changes tracking. Driven by Form's onValuesChange, which fires only
    // for user edits - the reload effect uses setFieldsValue, which does not
    // trigger it, so loading a section never marks it dirty.
    const [dirty, setDirty] = useState(false);

    // Fields drawn inside another field's row (see PairWith), so the render loop
    // can skip their standalone row.
    const pairedAway = getPairedAwayIds(section?.form?.formFields ?? []);

    const formRef = useRef<HTMLDivElement | null>(null);

    // Only mention the date-fill button on sections that actually have one, so the
    // help panel never describes a control the user cannot see.
    const hasDateFillSource = (section?.form?.formFields ?? []).some(isDateFillSource);
    useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);
    // A new section starts clean; its values arrive via the reload effect.
    useEffect(() => { setDirty(false); }, [section?.id]);

    // Clear the pending-transfer map when the section changes. It is module-level
    // (see its declaration), so without this a tick left behind in H1 would warn
    // when saving H2.
    useEffect(() => { pendingTransferRef.current.clear(); }, [section?.id]);


    useEffect(() => {
        if (!formValues) {
            antdForm.resetFields();
            antdForm.setFieldsValue(null);
            return;
        }

        let fv = formValues.formFieldValues?.reduce((pv, cv, i) => {
            // null means "explicitly not set"; "" means the value was cleared.
            // Both reload as empty, but only the former is a deliberate not-set.
            const storedNull = cv?.formValue === null || cv?.formValue === undefined;
            let raw = (cv?.formValue ?? "").toString().trim();
            let v: any = raw;

            // try to parse JSON arrays/objects stored as strings
            if (raw.startsWith('[') || raw.startsWith('{')) {
                try {
                    v = JSON.parse(raw);
                }
                catch {
                    v = raw.replace(/['"]+/g, '').trim();
                }
            }
            else {
                v = raw.replace(/['"]+/g, '').trim();
            }

            let ind = (cv.fieldId > 1000 ? cv.fieldId - 1000 : cv.fieldId);
            let field = findField(section, ind);

            // a stored value counts as "not set" only when the server sent null,
            // or the payload deserialises to an empty string / empty array
            const isEmpty = isEmptyValue(v);

            const isMulti = field?.fieldType == FieldTypeEnum.MultiSelect || field?.fieldType == FieldTypeEnum.TransferTable;

            if (field && !isEmpty) {
                // hydrate typed values so the antd control receives what it expects
                if (field.fieldType == FieldTypeEnum.DateTime) {
                    const d = dayjs(v);
                    v = d.isValid() ? d : null;
                }
                else if (field.fieldType == FieldTypeEnum.Double || field.fieldType == FieldTypeEnum.Integer) {
                    if (!Array.isArray(v)) {
                        const n = Number(v);
                        v = Number.isNaN(n) ? null : n;
                    }
                }
            }

            if (isMulti) {
                // A multi-select MUST reload as an array, never null: antd's
                // Select mode="multiple" ignores a null value and keeps whatever
                // is currently displayed, so a cleared selection would visibly
                // revert to the previous value after save.
                v = Array.isArray(v) ? v.filter(x => x !== null && x !== undefined && `${x}`.trim() !== '') : [];
            }

            if (field && field.allowNotSet) {
                // Tick the box when the stored value is null OR empty, per spec.
                // storedNull is the stronger signal (server sent an explicit null);
                // isEmpty also covers a value that was cleared to "".
                pv[`fieldmeta-${cv.fieldId}-notset`] = storedNull || isEmpty;
                pv[`field-${cv.fieldId}`] = isEmpty ? null : v;
            }
            else {
                pv[`field-${cv.fieldId}`] = (isEmpty && !isMulti) ? null : v;
            }

            return pv;
        }, {} as any);

        // reset first so fields absent from this payload drop their stale value
        // (including any leftover not-set checkbox state from a previous section)
        antdForm.resetFields();
        antdForm.setFieldsValue(fv);
    }, [section, formValues]);

    // skipPendingTransferCheck is set when the user has already been warned about
    // ticked-but-unmoved transfer rows and chose to save anyway.
    const formSubmit = (afterSave?: () => void, skipPendingTransferCheck = false) => {
        antdForm
            .validateFields()
            .then(values => {
                if (!section || !section?.form) {
                    return;
                }
                const blockingErrors: string[] = [];

                // Enumerate from the form definition, NOT from `values`. validateFields()
                // omits keys whose value is null/undefined, so a cleared field (ticked
                // "not set", or an emptied MultiSelect) would never reach the server and
                // the previous value would survive into the next reload.
                const keysToSave: number[] = [];
                section.form.formFields.forEach(ff => {
                    if (ff.fieldType == FieldTypeEnum.Label || ff.fieldType == FieldTypeEnum.Seperator) {
                        return;
                    }
                    keysToSave.push(ff.id);
                    // follow-up fields are rendered as a second control under id + 1000
                    if (ff.extraStyle?.includes("FollowUp")) {
                        keysToSave.push(ff.id + 1000);
                    }
                });

                var formFieldValues = keysToSave.map(key => {
                    const ind = (key > 1000 ? key - 1000 : key);
                    const field = findField(section, ind);
                    const v = antdForm.getFieldValue(`field-${key}`);
                    const notSet = !!antdForm.getFieldValue(`fieldmeta-${key}-notset`);

                    // A field hidden by its Conditional is not applicable: it must never
                    // block the save, and it is stored as an explicit null rather than
                    // whatever stale value is left in the store. Evaluated from the live
                    // form values via the same helper the visibility effect uses, so the
                    // saved state always matches what the user could actually see.
                    const hidden = field
                        ? !isConditionallyVisible(field, k => antdForm.getFieldValue(k))
                        : false;

                    if (hidden) {
                        return {
                            fieldId: key,
                            formValue: null,
                            isOtherValue: false as boolean
                        };
                    }

                    // A derived field is computed, never typed. It has no input to fill
                    // in and no not-set toggle to tick, so the "cannot be empty" check
                    // below would block the save with an instruction the investigator
                    // cannot follow - which is exactly what happened to eGFR once it
                    // became a CKD-EPI 2021 calculation while keeping AllowNotSet from
                    // when it was entered by hand.
                    //
                    // Its value is recomputed server-side on save anyway
                    // (ApplyDerivedFormulas), so whatever is sent here is advisory.
                    const derived = !!(field && getFormulaSpec(field));

                    if (field?.allowNotSet && !derived) {
                        // checked -> store null regardless of whatever is left in the input
                        if (notSet) {
                            return {
                                fieldId: key,
                                formValue: null,
                                isOtherValue: false as boolean
                            };
                        }

                        // unchecked -> an empty value is not allowed
                        if (isEmptyValue(v)) {
                            // full label here - this one appears in a toast, not inline
                            blockingErrors.push(`Το πεδίο "${field.label}" δεν μπορεί να είναι κενό. Επιλέξτε "${getAllowNotSetLabel(field)}" ή συμπληρώστε μια τιμή.`);
                        }
                    }

                    // dayjs objects must be serialised as ISO, not as their internal shape.
                    // Month-precision fields are pinned to the 15th per the protocol
                    // (ΕΕΕΕ-ΜΜ-15) and written as a plain date string: toISOString()
                    // would convert to UTC and, for east-of-Greenwich timezones, shift
                    // the stored date back a day.
                    //
                    // DayOptional fields are the exception: the whole point is that the
                    // investigator may know the real day, so the day they picked is kept
                    // verbatim. Pinning it to 15 here would throw away the very
                    // information the field was added to collect. When the day is not
                    // known they leave it at 15 and tick the companion flag.
                    const outValue = dayjs.isDayjs(v)
                        ? (isMonthPrecision(field) && !isDayOptional(field)
                            ? v.date(MONTH_PRECISION_DAY).format("YYYY-MM-DD")
                            : isDayOptional(field)
                                ? v.format("YYYY-MM-DD")
                                : v.toISOString())
                        : v;
                    // an explicitly cleared value must be persisted as empty so the next
                    // reload sees the clear instead of falling back to the stale value
                    const fv = isEmptyValue(outValue) ? "" : JSON.stringify(outValue);

                    return {
                        fieldId: key,
                        formValue: fv,
                        isOtherValue: false as boolean
                    }
                });

                if (blockingErrors.length > 0) {
                    message.error(blockingErrors[0]);
                    return;
                }

                // Drugs ticked on the left but never moved across are NOT recorded.
                // Warn rather than block: the tick may well be incidental, and the
                // investigator is the one who knows. But saving silently would store
                // "takes nothing" for a patient whose medicines are visibly selected
                // on screen, which is the kind of quiet data loss that only surfaces
                // at analysis.
                const pending = Array.from(pendingTransferRef.current.values());
                if (pending.length > 0 && !skipPendingTransferCheck) {
                    Modal.confirm({
                        title: "Υπάρχουν επιλογές που δεν έχουν καταχωρηθεί",
                        icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
                        width: 540,
                        content: (
                            <div>
                                <p style={{ marginTop: 8 }}>
                                    Σε {pending.length === 1 ? "ένα πεδίο" : `${pending.length} πεδία`} έχετε
                                    επιλέξει φάρμακα στην αριστερή στήλη χωρίς να τα μεταφέρετε στα επιλεγμένα.
                                    Αυτά <strong>δεν θα αποθηκευτούν</strong>.
                                </p>
                                <ul style={{ paddingInlineStart: 20 }}>
                                    {pending.map(p => (
                                        <li key={p.label}>{p.label}: {p.count} επιλεγμένα</li>
                                    ))}
                                </ul>
                            </div>
                        ),
                        okText: "Αποθήκευση χωρίς αυτά",
                        okButtonProps: { danger: true },
                        cancelText: "Επιστροφή",
                        onOk: () => formSubmit(afterSave, true),
                    });
                    return;
                }

                var saveForm: SaveFormDataVM = {
                    formFieldValues: formFieldValues,
                    formId: section?.form.id,
                    sectionId: section?.id,
                    patientId: context.selection?.patient?.id ?? 0,
                    patientAdmissionId: context.selection?.admission?.id ?? 0
                }

                setSaving(true);
                saveFormValues(saveForm).then((r) => {
                    message.success("Αποθηκεύτηκε");
                    setDirty(false);
                    formSaved();
                    // Only advance once the save has actually succeeded - moving on
                    // after a failed save would silently discard the section.
                    afterSave?.();
                })
                    .catch(err => {
                        message.error("Σφάλμα κατα την αποθήκευση των δεδομένων" + err);
                    })
                    .finally(() => {
                        setSaving(false);
                    });

            })
            .catch(info => {
                setSaving(false);
                console.log('Τα δεδομένα δεν είναι έγκυρα', info);
            });
    }

    // The form's data-entry controls in DOM order. Deliberately narrow: it excludes
    // the not-set toggles and the inquiry buttons, so Enter walks the fields a user
    // is actually filling rather than every focusable thing on the page.
    const getEntryFields = (root: HTMLElement | null): HTMLElement[] => {
        if (!root) return [];
        const nodes = root.querySelectorAll<HTMLElement>(
            'input:not([type="hidden"]):not([disabled]), textarea:not([disabled])'
        );
        return Array.from(nodes).filter(el => el.offsetParent !== null);
    };

    // Put the caret on the first unanswered field once the section's values have
    // loaded, so work resumes where it stopped rather than at the top. Runs once
    // per section: re-running on every value change would yank focus away mid-typing.
    const autoFocusedSection = useRef<number | null>(null);
    useEffect(() => {
        const sectionId = section?.id;
        if (sectionId == null || disabled) return;
        if (autoFocusedSection.current === sectionId) return;
        // wait for the reload effect to populate the store
        if (formValues === undefined) return;

        autoFocusedSection.current = sectionId;

        // after paint, so the controls exist and antd has applied its values
        const t = window.setTimeout(() => {
            const fields = getEntryFields(formRef.current);
            const firstEmpty = fields.find(el =>
                (el as HTMLInputElement).value === "" && !(el as HTMLInputElement).readOnly);
            // Only when something is actually empty - on a completed section,
            // stealing focus would scroll the user somewhere they did not ask to be.
            firstEmpty?.focus();
        }, 0);
        return () => window.clearTimeout(t);
    }, [section?.id, formValues, disabled]);

    // Enter advances to the next field instead of submitting.
    //
    // A long section is a column of numbers copied off a lab report; making the
    // user reach for Tab (or the mouse) between each one is most of the cost of
    // filling it in. Ctrl+Enter remains save, so the habit of "Enter submits" is
    // preserved where it matters.
    const focusNextField = (from: EventTarget | null) => {
        const form = formRef.current;
        const fields = getEntryFields(form);
        const i = fields.indexOf(from as HTMLElement);
        if (i === -1) return false;

        const next = fields[i + 1];
        if (!next) return false;

        next.focus();
        // Select rather than place a caret: the next action is almost always to
        // overwrite, and a pre-selected value makes typing replace it.
        if (next instanceof HTMLInputElement && next.type !== "checkbox" && next.type !== "radio") {
            next.select?.();
        }
        return true;
    };

    // Pasting a column of values fills this field and the ones below it.
    //
    // Lab results arrive as a column - copied from a report, a spreadsheet or a LIS
    // export - and typing 24 of them one at a time is the bulk of the work in
    // Section I. Accepts a plain column, an Excel column (CRLF), a two-column
    // name+value paste (the value is taken from the last cell), Greek decimal
    // commas, and values with units glued on.
    const onFormPaste = (e: React.ClipboardEvent) => {
        const text = e.clipboardData?.getData("text") ?? "";
        // A single value is an ordinary paste - leave it to the browser.
        if (!/[\n\r]/.test(text)) return;

        const target = e.target as HTMLElement;
        // Only from a plain text/number input; a date or select paste means
        // something else entirely.
        if (!(target instanceof HTMLInputElement)) return;
        if (target.closest(".ant-picker, .ant-select, .ant-transfer")) return;

        const values = text
            .replace(/\r\n?/g, "\n")
            .split("\n")
            .map(line => {
                const cells = line.split("\t");
                const m = `${cells[cells.length - 1]}`.trim().match(/-?\d+(?:[.,]\d+)?/);
                // "" keeps a blank line occupying its row, so a report with a gap
                // stays aligned with the fields rather than shifting everything up.
                return m ? m[0].replace(",", ".") : "";
            });

        // Walk only fields of the same kind as the one being pasted into. Section I
        // interleaves each lab value with its measurement date, so a naive walk down
        // the DOM would drop every second pasted number into a date picker.
        const isNumericEntry = (el: HTMLElement) =>
            el instanceof HTMLInputElement
            && !el.closest(".ant-picker, .ant-select, .ant-transfer");

        const fields = getEntryFields(formRef.current).filter(isNumericEntry);
        const start = fields.indexOf(target);
        if (start === -1) return;

        e.preventDefault();

        // Map each pasted line onto the field at the same offset, and remember
        // what was overwritten so the whole paste can be undone in one step.
        const undo: { key: string; prev: any }[] = [];
        let filled = 0;

        values.forEach((v, i) => {
            const el = fields[start + i] as HTMLInputElement | undefined;
            if (!el || v === "") return;

            // The antd form field name lives on the input's id (`field-<id>`).
            const key = el.id;
            if (!key) return;

            undo.push({ key, prev: antdForm.getFieldValue(key) });
            const num = Number(v);
            antdForm.setFieldValue(key, Number.isFinite(num) ? num : v);
            filled++;
        });

        if (filled === 0) return;

        setDirty(true);
        antdForm.validateFields(undo.map(u => u.key)).catch(() => { });

        const overflow = values.filter(v => v !== "").length - filled;
        message.success({
            content: (
                <span>
                    Συμπληρώθηκαν {filled} πεδία
                    {overflow > 0 ? ` (${overflow} τιμές δεν χώρεσαν)` : ""}
                    {" "}
                    <a onClick={() => {
                        undo.forEach(u => antdForm.setFieldValue(u.key, u.prev));
                        antdForm.validateFields(undo.map(u => u.key)).catch(() => { });
                        message.info("Η επικόλληση αναιρέθηκε.");
                    }}>Αναίρεση</a>
                </span>
            ),
            duration: 8,
        });
    };

    // Ctrl/Cmd+Enter saves. Scoped to this form rather than the window so it cannot
    // fire from elsewhere on the page, and gated on the same conditions as the save
    // button - including `saving`, since holding the shortcut would otherwise post
    // several times and each POST creates a new FormData revision.
    const onFormKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== "Enter") {
            return;
        }

        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            if (disabled || saving) {
                return;
            }
            formSubmit();
            return;
        }

        // A DatePicker, Select or Transfer uses plain Enter for its own popup
        // (confirm the highlighted date/option), so those must keep it.
        const el = e.target as HTMLElement | null;
        if (el?.closest(".ant-picker, .ant-select, .ant-transfer")) {
            return;
        }

        if (focusNextField(e.target)) {
            e.preventDefault();
        }
    };

    return (<>
        {section?.form &&
            // Forms.Name, not Forms.Description: the name is "<protocol> — <section>"
            // (e.g. "1378-0071 — D. Δημογραφικά / …") which is what the card should
            // say. Description is a prose sentence that repeats the section letter
            // and, for H1/H2/H3, is identical across all three ("Ενότητα H του CRF").
            <Card title={`${section?.form.name ?? ""}` + (section?.parentId != null ? ` - ${section.name}` : "")}
                extra={
                    <Space>
                        {dirty && <Tag color="warning" style={{ marginInlineEnd: 0 }}>Μη αποθηκευμένες αλλαγές</Tag>}
                        <Popover
                            placement="bottomRight"
                            trigger="click"
                            title="Συντομεύσεις & γρήγορη καταχώρηση"
                            content={<ShortcutHelp hasDateFill={hasDateFillSource} />}
                        >
                            <Tooltip title="Συντομεύσεις">
                                <Button type="text" icon={<QuestionCircleOutlined />} />
                            </Tooltip>
                        </Popover>
                        <Tooltip title="Ctrl + Enter">
                            <Button type={"primary"} disabled={disabled} loading={saving} onClick={() => { formSubmit(); }}>Αποθηκευση</Button>
                        </Tooltip>
                        {onSaveAndNext && (
                            <Tooltip title={nextSectionName ? `Επόμενη: ${nextSectionName}` : "Τελευταία ενότητα"}>
                                {/* span wrapper: a disabled antd Button swallows pointer
                                    events, so the tooltip would not show on the last section */}
                                <span>
                                    <Button
                                        disabled={disabled || saving || !nextSectionName}
                                        onClick={() => { formSubmit(onSaveAndNext); }}
                                    >
                                        Αποθήκευση και επόμενο →
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Space>
                }>
                {/* Horizontal (label beside input) on desktop: it keeps the form on a
                    single screen, which matters more than perfect left-alignment when
                    an investigator fills this repeatedly. Below md the label column
                    leaves too little room for the input, so it stacks instead.

                    The wrapping div carries the DOM ref: antd's Form does not forward
                    one, and the keyboard helpers need to query the rendered inputs. */}
                <div ref={formRef}>
                <Form form={antdForm}
                    layout={isMobile ? "vertical" : "horizontal"}
                    labelAlign="left"
                    labelWrap
                    wrapperCol={isMobile ? undefined : { flex: 1 }}
                    className="ecrf-form"
                    onKeyDown={onFormKeyDown}
                    onPaste={onFormPaste}
                    onValuesChange={() => setDirty(true)}
                    colon={false}>
                    {groupFields(orderFieldsWithDerived(section.form.formFields))
                        .map((group, gi) => (
                            <div key={`group-${gi}`} className="form-field-group">
                                {group.title && (
                                    <div className="form-field-group-title">{group.title}</div>
                                )}
                                {group.fields
                                    // A paired partner is drawn inside its owner's
                                    // row, so it must not also get a row of its own.
                                    .filter(ff => !pairedAway.has(ff.id))
                                    .map(ff => (
                                        <FormFieldRender
                                            key={ff.id}
                                            formField={ff}
                                            section={section}
                                            allFormValues={allFormValues}
                                            pendingInquiries={pendingInquiries}
                                            highlightedFieldId={highlightedFieldId}
                                            onInquiryResolved={onInquiryResolved}
                                        />
                                    ))}
                            </div>
                        ))}
                </Form>
                </div>
            </Card>}
    </>
    );
}

export const FormFieldRender: React.FC<{
    formField: FormField,
    section: EcrfSection,
    allFormValues?: FormData[] | null,
    pendingInquiries?: Inquiry[],
    highlightedFieldId?: number,
    onInquiryResolved?: (id: number) => void,
    /** Render the control without its label. Used when this field is drawn as the
     *  secondary half of a paired row (see PairWith) and the owner's label already
     *  names the observation. */
    hideLabel?: boolean,
}> = ({ formField, section, allFormValues, pendingInquiries, highlightedFieldId, onInquiryResolved, hideLabel }) => {
    const antdForm = useFormInstance();
    const [extraStyle, setExtraStyle] = useState<{ FollowUp: boolean, Conditional?: { fieldId: number, value: string | string[] }, AllowNotSetLabel?: string, DateTimeFormat?: string, ShowTime?: boolean, DerivedCategories?: boolean, AllowFuture?: boolean, NotAfterField?: number } | null>(null);
    const [visible, setVisible] = useState<boolean>(true);
    const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
    // Conditional visibility now reads its controlling field from the whole-store
    // watch (see the Conditional effect below), so no per-id watch is needed for it.
    const field96 = useWatch('field-96', antdForm);
    const notSetWatch = useWatch(`fieldmeta-${formField.id}-notset`, antdForm);
    const notSetWatchSecondary = useWatch(`fieldmeta-${formField.id + 1000}-notset`, antdForm);

    // For a derived-categories field: the field it derives from. Resolved from the
    // form definition (the source declares CategoryTargetFieldId pointing here).
    const derivedSourceField = section?.form?.formFields?.find(
        ff => getCategoryTargetFieldId(ff) === formField.id
    ) ?? null;
    // Watch the source's selection so the derived display updates as the user picks.
    // Hooks cannot be conditional, so this watches a harmless key when not derived.
    const derivedSourceValue = useWatch(
        derivedSourceField ? `field-${derivedSourceField.id}` : `field-${formField.id}`,
        antdForm
    );

    // The field to draw on this row alongside our own control, if any.
    const pairedFieldId = getPairWithId(formField);
    const pairedField = pairedFieldId != null
        ? (section?.form?.formFields?.find(ff => ff.id === pairedFieldId) ?? null)
        : null;

    // For a formula field: its spec, and the live computed value. A formula can read
    // any number of sources, so rather than a variable list of hooks (illegal), watch
    // the whole store and pick the operands out of it.
    const formulaSpec = getFormulaSpec(formField);
    // True for any field the server computes: formula results and derived
    // categories. These are read-only and never user-entered.
    const isDerived = !!formulaSpec || extraStyle?.DerivedCategories === true;
    const allValues = useWatch([], antdForm) as any;
    // Raw values: each formula interprets its own operands (numbers or dates).
    const formulaValue = formulaSpec
        ? computeFormula(
            formulaSpec.formula,
            formulaSpec.sources.map(id => allValues?.[`field-${id}`]),
            formulaSpec.decimals,
            formulaSpec.months,
            formulaSpec.codes
        )
        : undefined;

    // A formula whose operand comes from another section (a mirror field) cannot be
    // completed here when that source has never been filled in. Surfaced separately
    // from the ordinary "waiting" state because the user has to go to the other
    // section to resolve it - waiting on this form would never help.
    const formulaBlockedBy = (() => {
        if (!formulaSpec || formulaValue !== null) return null;

        const missingMirror = formulaSpec.sources
            .map(id => findField(section, id))
            .find(src => isMirrorField(src) && isEmptyValue(allValues?.[`field-${src!.id}`]));

        if (!missingMirror) return null;

        return `Απαιτείται πρώτα η συμπλήρωση του πεδίου "${missingMirror.label}" σε προηγούμενη ενότητα.`;
    })();

    // Clearing must happen only on a real user toggle. Running it off the watched
    // value alone re-fires on reload/re-render and wipes freshly loaded data.
    const onNotSetToggle = (fieldKey: number) => (e: any) => {
        const checked = !!e?.target?.checked;
        if (checked) {
            antdForm.setFieldValue(`field-${fieldKey}`, null);
        }
        // revalidate so the "cannot be empty" feedback clears/appears immediately
        antdForm.validateFields([`field-${fieldKey}`]).catch(() => { });

        // A lab value and its measurement date are one observation: if the value
        // was not recorded there is no date to record either, and vice versa.
        // Ticking one used to leave the other demanding an entry that does not
        // exist, so the investigator had to tick both by hand on 24 pairs.
        //
        // Only the owner drives the pair. The date half carries no PairWith of its
        // own, so this cannot recurse.
        const partnerId = getPairWithId(formField);
        if (partnerId == null || fieldKey !== formField.id) {
            return;
        }

        antdForm.setFieldValue(`fieldmeta-${partnerId}-notset`, checked);
        if (checked) {
            antdForm.setFieldValue(`field-${partnerId}`, null);
        }
        antdForm.validateFields([`field-${partnerId}`]).catch(() => { });
    };

    const pendingInquiry = pendingInquiries?.find(i => i.fieldId === formField.id) ?? null;
    const isHighlighted = highlightedFieldId === formField.id;

    useEffect(() => {
        if (!isHighlighted) return;
        const el = document.getElementById(`field-inquiry-${formField.id}`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [isHighlighted]);

    useEffect(() => {
        if (!formField?.extraStyle) {
            return;
        }

        try {
            let xt = JSON.parse(formField.extraStyle);
            let hasFollowUp = xt.FollowUp == true;
            let conditional = xt?.Conditional;
            if (conditional) {
                setVisible(false);
            }
            const allowNotSetLabel = xt?.AllowNotSetLabel ?? xt?.AllowNotSetLabelText ?? null;
            const dateTimeFormat = xt?.DateTimeFormat ?? null;
            const showTime = xt?.ShowTime === true;
            // Marks a field whose value is derived server-side (the categories of
            // another field's selection). Rendered read-only - the server overwrites
            // whatever the client sends for it.
            const derivedCategories = xt?.DerivedCategories === true;
            // Opt-in exemption from the no-future-dates rule, for dates that record
            // something scheduled rather than something observed.
            const allowFuture = xt?.AllowFuture === true;
            const notAfterField = typeof xt?.NotAfterField === "number" ? xt.NotAfterField : undefined;

            setExtraStyle({ FollowUp: hasFollowUp, Conditional: conditional as any, AllowNotSetLabel: allowNotSetLabel, DateTimeFormat: dateTimeFormat, ShowTime: showTime, DerivedCategories: derivedCategories, AllowFuture: allowFuture, NotAfterField: notAfterField });

        }
        catch {
            console.log("exception", formField.extraStyle);
            return;
        }

    }, [formField]);

    // Conditional visibility: {"Conditional":{"fieldId":N,"value":"1"}} shows this
    // field only while field N holds that value. Reads the controlling field from the
    // whole-store watch rather than a per-id useWatch, so it works for ANY field id -
    // it was previously hardcoded to 60 and 63 and silently did nothing for others.
    useEffect(() => {
        const conditional = extraStyle?.Conditional;
        if (!conditional?.fieldId) {
            return;
        }

        // Same helper formSubmit uses, so what is shown and what is saved agree.
        const nowVisible = isConditionallyVisible(formField, k => allValues?.[k]);

        setVisible(prev => {
            // Clear the value when the field goes from shown to hidden. formSubmit
            // enumerates the form definition rather than what is rendered, so without
            // this a hidden field keeps posting its stale value - e.g. answering "no
            // documented CKD diagnosis" while still storing a CKD diagnosis date.
            //
            // Guarded on a real transition: running it whenever `nowVisible` is false
            // would wipe values loaded from the server before the user touched
            // anything.
            if (prev && !nowVisible) {
                antdForm.setFieldValue(`field-${formField.id}`, null);
                antdForm.setFieldValue(`fieldmeta-${formField.id}-notset`, false);
            }
            return nowVisible;
        });
    }, [allValues, extraStyle]);

    useEffect(() => {
        if (field96?.length > 0) {
            antdForm.setFieldValue('field-55', null);
            antdForm.validateFields(["field-55"]);
        }
    }, [field96]);

    const buttonCalculateClick = (field: any) => {
        if (field.label == "ΒΜΙ") {
            let bmi = bmiCalculation(section, antdForm);
            if (bmi == null) {
                return;
            }
            antdForm.setFieldValue(`field-${field.id}`, parseFloat(bmi.toFixed(2)));
        }
        else if (field.label == "e-GFR") {
            let egfr = egfrCalculation(section, antdForm, allFormValues ?? null);
            if (egfr == null) {
                return;
            }

            antdForm.setFieldValue(`field-${field.id}`, parseFloat(egfr.toFixed(2)));
        }
    }

    // Copies this field's date into every OTHER date field on the section that is
    // still empty. Deliberately never overwrites: a lab drawn on a different day is
    // entered by hand, and silently replacing it would be worse than the typing it
    // saves. Fields marked "not set" are left alone for the same reason.
    const fillEmptyDates = () => {
        const source = antdForm.getFieldValue(`field-${formField.id}`);
        if (isEmptyValue(source)) {
            message.warning("Συμπληρώστε πρώτα αυτή την ημερομηνία.");
            return;
        }

        const targets = getDateFillTargets(section, formField.id);
        const filled: string[] = [];

        targets.forEach(t => {
            const key = `field-${t.id}`;
            if (!isEmptyValue(antdForm.getFieldValue(key))) return;
            if (antdForm.getFieldValue(`fieldmeta-${t.id}-notset`)) return;
            antdForm.setFieldValue(key, source);
            filled.push(key);
        });

        if (filled.length === 0) {
            message.info("Δεν υπάρχουν κενές ημερομηνίες προς συμπλήρωση.");
            return;
        }

        // revalidate the touched fields so any bound rule updates immediately
        antdForm.validateFields(filled).catch(() => { });
        message.success(`Συμπληρώθηκαν ${filled.length} ημερομηνίες.`);
    };

    // Rendered as a sibling of the field's Form.Item (see renderFieldRow), never as
    // part of its child tree - see the note on withNotSet.
    // Compact icon toggle rather than a labelled checkbox. The label ("Δεν
    // μετρήθηκε" etc.) repeats on every measurement field, and six copies of it
    // crowd the right-hand side of the form; the text moves into the tooltip and
    // the active state is conveyed by colour + a filled icon.
    const getAllowNotSetCheckbox = (suffix: number = 0) => {
        const label = extraStyle?.AllowNotSetLabel ?? DEFAULT_NOT_SET_LABEL;
        return (
            <Form.Item
                name={`fieldmeta-${formField.id + suffix}-notset`}
                valuePropName="checked"
                noStyle
                initialValue={false}
            >
                <NotSetToggle
                    label={label}
                    onToggle={onNotSetToggle(formField.id + suffix)}
                />
            </Form.Item>
        );
    };

    // Puts the not-set checkbox inline to the right of an already-rendered Form.Item.
    // Takes the whole Form.Item as a sibling so the bound control stays the Form.Item's
    // direct child (see withNotSet). Non-AllowNotSet fields are returned untouched so
    // their layout and vertical rhythm are completely unaffected.
    const renderWithNotSet = (item: ReactNode, suffix: number = 0) => {
        // Text/Integer/Double carry the checkbox inside the input's addonAfter slot,
        // so they need no outer layout at all. Only DatePicker does, because antd's
        // DatePicker has no addonAfter prop.
        //
        // A date-fill source needs the same sibling row to hang its button off, even
        // when the field itself has no not-set toggle.
        const wantsFillButton = isDateFillSource(formField) && suffix === 0;
        const wantsNotSet = formField.allowNotSet && formField.fieldType == FieldTypeEnum.DateTime;
        if (!wantsNotSet && !wantsFillButton) return item;
        return (
            // flex-start (not baseline): the Form.Item's baseline shifts with its label
            // and any validation message, which left the checkboxes at different
            // heights from row to row. Anchoring to the top and pinning the checkbox to
            // one control-height row lines them all up on the same vertical axis.
            //
            // gap 0: the toggle is styled as an addon attached to the picker, so it
            // must sit flush against it rather than floating alongside.
            <div className="allow-notset-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
                {/* Sized to the control rather than flex:1. Stretching this to fill the
                    row pushed the toggle out to the far right edge, visually detached
                    from the picker it belongs to. minWidth:0 still lets it shrink so a
                    long validation message cannot push the toggle onto a new line. */}
                <div className="allow-notset-control" style={{ flex: '0 1 auto', minWidth: 0 }}>{item}</div>
                {wantsNotSet && (
                    <div className="allow-notset-toggle-slot">
                        {getAllowNotSetCheckbox(suffix)}
                    </div>
                )}
                {wantsFillButton && (
                    // Same slot and styling as the not-set toggle, so it reads as a
                    // second addon attached to the picker rather than a detached
                    // button. Icon-only: a downward arrow says "apply to the fields
                    // below", and the tooltip carries the detail.
                    <div className="allow-notset-toggle-slot">
                        <Tooltip title="Αντιγραφή αυτής της ημερομηνίας σε όλες τις κενές ημερομηνίες της ενότητας. Δεν αντικαθιστά όσες έχουν ήδη συμπληρωθεί.">
                            <button
                                type="button"
                                aria-label="Συμπλήρωση κενών ημερομηνιών"
                                className="notset-toggle ecrf-fill-toggle"
                                onClick={fillEmptyDates}
                            >
                                <VerticalAlignBottomOutlined />
                            </button>
                        </Tooltip>
                    </div>
                )}
            </div>
        );
    };

    const getFieldComponent = (hasUnits: boolean = true, placeholder: string | undefined = undefined, suffix: number = 0) => {
        const notSetChecked = !!(suffix === 0 ? notSetWatch : notSetWatchSecondary);

        // Shows the accepted range up front so the user avoids the error rather than
        // being told about it after typing. Only used when no explicit placeholder is
        // passed (the follow-up columns supply their own).
        //
        // Suppressed while "not set" is ticked: a greyed control still showing
        // "3–20" or "Επιλέξτε ημερομηνία" reads as though it holds a value, which is
        // the opposite of what the toggle just recorded. An empty disabled control
        // says "deliberately blank".
        const rangePlaceholder = notSetChecked ? undefined : (() => {
            const { min, max } = formField;
            if (min != null && max != null) return `${min}–${max}`;
            if (min != null) return `≥ ${min}`;
            if (max != null) return `≤ ${max}`;
            return undefined;
        })();

        // The passed-in placeholder (follow-up column headers, and the DatePicker's
        // own "Επιλέξτε ημερομηνία") is suppressed on the same grounds.
        const effectivePlaceholder = notSetChecked ? "" : placeholder;

        // Fields whose value the server derives (e.g. the categories of a
        // MultiSelect/TransferTable selection). Read-only by design: the server
        // recomputes and overwrites this on every save, so an editable control
        // would imply the user's input matters when it does not.
        //
        // When the source field is on this form we compute the categories live from
        // its current selection, so the display tracks the user's edits immediately.
        // Otherwise we fall back to the stored value injected by the Form.Item.
        if (extraStyle?.DerivedCategories) {
            return (
                <DerivedCategoriesDisplay
                    liveValue={derivedSourceField
                        ? deriveCategories(derivedSourceValue, derivedSourceField.listValues)
                        : undefined}
                />
            );
        }

        // Same contract for a numeric formula field (BMI, WHR): read-only, recomputed
        // live from its sources, and overwritten by the server on save.
        if (formulaSpec) {
            // A flag formula yields a yesno code; show the label the user would
            // have picked, not a bare 0/1.
            if (isFlagFormula(formulaSpec.formula)) {
                const label = formulaValue === null || formulaValue === undefined
                    ? undefined
                    : (formulaValue === 1 ? "Ναι" : "Όχι");
                return <DerivedFlagDisplay label={label} isSet={formulaValue === 1} />;
            }
            return <DerivedFormulaDisplay liveValue={formulaValue} units={formField.units} blockedBy={formulaBlockedBy} />;
        }

        // Builds the addonAfter content: the units (when present) followed by the
        // not-set checkbox. Living in the addon slot means antd gives it the standard
        // gray addon background and shares the input's border, and it can no longer
        // affect row height or wrap onto its own line.
        // Safe w.r.t. form binding: addonAfter is a sibling of the input's value, not
        // a wrapper around the control, so value/onChange injection is unaffected.
        const buildAddonAfter = (units: ReactNode) => {
            const hasUnitsContent = units !== null && units !== undefined && units !== '';
            if (!formField.allowNotSet) {
                return hasUnitsContent ? units : undefined;
            }

            return (
                // flexShrink:0 on the units span, not just nowrap: inside a flex
                // container a text child may shrink below its content width and
                // break anyway. A compound unit like "×10³/µL" wrapping to two
                // lines makes that one input taller than the rest of the column.
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                    {hasUnitsContent && <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{units}</span>}
                    {hasUnitsContent && <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--ant-color-split, rgba(5,5,5,0.06))', flexShrink: 0 }} />}
                    {getAllowNotSetCheckbox(suffix)}
                </span>
            );
        };

        // Returns the control untouched, by design. antd clones the Form.Item's single
        // child to inject value/onChange, so wrapping the control in ANY element or
        // Fragment here makes that wrapper absorb the injected props and the input is
        // never bound to the form (it then always saves as null). This is what the
        // earlier Space.Compact layout got wrong.
        // The not-set checkbox is rendered as a sibling of the Form.Item instead -
        // see the renderWithNotSet helper below. Do not turn this into a wrapper.
        const withNotSet = (control: ReactNode) => control;

        switch (formField.fieldType) {
            case FieldTypeEnum.Text:
                return withNotSet(
                    <Input
                        addonAfter={buildAddonAfter(hasUnits ? formField.units : null)}
                        placeholder={effectivePlaceholder}
                        disabled={notSetChecked}
                    />
                );
            case FieldTypeEnum.CheckBox:
                // Same dual-format support as RadioButton below.
                return (<Checkbox.Group>
                    {parseOptionPairs(formField.listValues).map(opt => (
                        <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                    ))}
                </Checkbox.Group>);
            case FieldTypeEnum.RadioButton:
                // Accepts JSON options (value distinct from label, for codelists) or a
                // plain comma-separated list. See parseOptionPairs.
                return (
                    <Radio.Group>
                        {parseOptionPairs(formField.listValues).map(opt => (
                            <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
                        ))}
                    </Radio.Group>
                );
            case FieldTypeEnum.ListBox:
                // Same dual-format support as RadioButton above.
                return (
                    <Select>
                        {parseOptionPairs(formField.listValues).map(opt => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Select.Option>
                        ))}
                    </Select>
                );
            case FieldTypeEnum.MultiSelect:
                {
                    let options: any[] = [];
                    if (formField.listValues) {
                        let lv = formField.listValues.trim();
                        if (lv.startsWith('[') || lv.startsWith('{')) {
                            try { options = JSON.parse(lv); } catch { options = formField.listValues.split(',').map(o => o.trim()); }
                        }
                        else {
                            options = formField.listValues.split(',').map(o => o.trim());
                        }
                    }

                    return (
                            <Select
                            mode="multiple"
                            showSearch
                            allowClear
                            maxTagCount="responsive"
                            optionFilterProp="label"
                            placeholder={effectivePlaceholder}
                            // value/onChange are injected by the wrapping Form.Item -
                            // setting value here would freeze the control on reload
                            // custom filter to search across label and category/description
                            filterOption={(input, option:any) => {
                                try {
                                    const text = (option.children ?? '').toString().toLowerCase();
                                    const category = (option['data-category'] ?? '').toString().toLowerCase();
                                    const description = (option['data-description'] ?? '').toString().toLowerCase();
                                    const needle = input.toString().toLowerCase();
                                    return text.indexOf(needle) > -1 || category.indexOf(needle) > -1 || description.indexOf(needle) > -1;
                                } catch { return false; }
                            }}
                        >
                            {options.filter(opt => {
                                if (typeof opt !== 'object') return `${opt}`.trim() !== '';
                                const val = opt.value ?? opt.id ?? opt.key;
                                return val !== undefined && val !== null && `${val}`.trim() !== '';
                            }).map((opt, i) => {
                                const val = (typeof opt === 'object') ? (opt.value ?? opt.id ?? opt.key ?? JSON.stringify(opt)) : opt;
                                const labelText = (typeof opt === 'object') ? (opt.label ?? opt.name ?? opt.value ?? JSON.stringify(opt)) : opt;
                                const category = (typeof opt === 'object') ? (opt.category ?? null) : null;
                                const label = category ? `${labelText} (${category})` : labelText;
                                // pass metadata as props so filterOption can read them
                                return <Select.Option key={val} value={val} label={label} data-category={(typeof opt === 'object') ? (opt.category ?? '') : ''} data-description={(typeof opt === 'object') ? (opt.description ?? '') : ''}>{label}</Select.Option>
                            })}
                        </Select>
                    );
                }
            case FieldTypeEnum.TransferTable:
                {
                    let options: any[] = [];
                    if (formField.listValues) {
                        let lv = formField.listValues.trim();
                        if (lv.startsWith('[') || lv.startsWith('{')) {
                            try { options = JSON.parse(lv); } catch { options = formField.listValues.split(',').map(o => o.trim()); }
                        }
                        else {
                            options = formField.listValues.split(',').map(o => o.trim());
                        }
                    }

                    const rawRows = options.map((opt, i) => {
                        const key = (typeof opt === 'object') ? (opt.value ?? opt.id ?? `k-${i}`) : `${opt}`;
                        const title = (typeof opt === 'object') ? (opt.label ?? opt.name ?? opt.value ?? `${opt}`) : `${opt}`;
                        const description = (typeof opt === 'object') ? (opt.description ?? '') : '';
                        const category = (typeof opt === 'object') ? (opt.category ?? '') : '';
                        return { key: key.toString(), title, description, category };
                    });

                    // Sorted here rather than in the stored ListValues, so every centre
                    // and every list added later is ordered without a data migration.
                    //
                    // Three rules, in order:
                    //   1. Group by category, keeping the order the categories first
                    //      appear in the JSON. That order is clinical (biguanides,
                    //      sulfonylureas, ... insulins), and alphabetising it would
                    //      scatter related drug classes.
                    //   2. Within a category, drugs A-Z. localeCompare with 'el' so
                    //      Greek labels collate correctly - a plain < comparison sorts
                    //      by code point and puts every Greek name after every Latin one.
                    //   3. The per-category "δεν έχει καταγραφεί" entry last within its
                    //      own category. It is the fallback, so it reads as the end of
                    //      that group rather than competing with the real drugs; before
                    //      this they were all appended after the entire list.
                    const categoryOrder = new Map<string, number>();
                    rawRows.forEach(r => {
                        if (!categoryOrder.has(r.category)) categoryOrder.set(r.category, categoryOrder.size);
                    });

                    const isFallbackRow = (key: string) => key.includes("_unknown_");

                    const dataSource = [...rawRows].sort((a, b) => {
                        const ca = categoryOrder.get(a.category) ?? 0;
                        const cb = categoryOrder.get(b.category) ?? 0;
                        if (ca !== cb) return ca - cb;

                        const fa = isFallbackRow(a.key) ? 1 : 0;
                        const fb = isFallbackRow(b.key) ? 1 : 0;
                        if (fa !== fb) return fa - fb;

                        return a.title.localeCompare(b.title, 'el', { sensitivity: 'base' });
                    });

                    // Description is dropped when no option supplies one - an always-on
                    // empty column just steals width from the label. It stays part of
                    // the search text either way (see filterOption below), so a list
                    // that does carry descriptions is still searchable by them.
                    const hasDescription = dataSource.some(d => `${d.description}`.trim() !== '');
                    const hasCategory = dataSource.some(d => `${d.category}`.trim() !== '');

                    const leftColumns = [
                        { title: 'Τιμή', dataIndex: 'title' },
                        ...(hasCategory ? [{ title: 'Κατηγορία', dataIndex: 'category' }] : []),
                        ...(hasDescription ? [{ title: 'Περιγραφή', dataIndex: 'description' }] : []),
                    ];

                    const rightColumns = leftColumns;

                    const TableTransfer: React.FC<any> = ({ value, onChange }) => {
                        const [targetKeys, setTargetKeys] = useState<string[]>((value as any) || []);
                        useEffect(() => { setTargetKeys((value as any) || []); }, [value]);

                        const onTChange = (nextTargetKeys: React.Key[], direction?: any, moveKeys?: React.Key[]) => {
                            setTargetKeys((nextTargetKeys as string[]) || []);
                            onChange?.((nextTargetKeys as string[]) || []);
                            // Anything moved across is no longer pending.
                            pendingTransferRef.current.delete(formField.id);
                        };

                        return (
                            <Transfer
                                dataSource={dataSource}
                                showSearch
                                // Hides antd's select-all checkbox in each panel
                                // header. On a 57-drug list "select all" selects
                                // whatever the search currently shows, which is an
                                // easy way to move dozens of medicines a patient does
                                // not take; nobody has a use for it here.
                                showSelectAll={false}
                                titles={["Διαθέσιμα", "Επιλεγμένα"]}
                                targetKeys={targetKeys}
                                onChange={onTChange}
                                filterOption={(input, item) => {
                                    const txt = `${item.title} ${item.category} ${item.description}`.toLowerCase();
                                    return txt.indexOf(input.toLowerCase()) > -1;
                                }}
                            >
                                {({ direction, filteredItems, onItemSelectAll, onItemSelect, selectedKeys }) => {
                                    const columns = direction === 'left' ? leftColumns : rightColumns;

                                    // Ticking a drug on the left selects it but does
                                    // NOT record it - it still has to be moved across
                                    // with the arrow. That is easy to miss, and the
                                    // form would save as though the patient took
                                    // nothing. Tracked here so formSubmit can warn.
                                    if (direction === 'left') {
                                        if (selectedKeys.length > 0) {
                                            pendingTransferRef.current.set(formField.id, {
                                                label: formField.label,
                                                count: selectedKeys.length,
                                            });
                                        }
                                        else {
                                            pendingTransferRef.current.delete(formField.id);
                                        }
                                    }

                                    const rowSelection = {
                                        onSelectAll(selected: boolean, selectedRows: any[]) {
                                            const treeSelectedKeys = selectedRows.map(({ key }) => key);
                                            // onItemSelectAll expects (keys: Key[], selected: boolean)
                                            onItemSelectAll(treeSelectedKeys, selected);
                                        },
                                        onSelect({ key }: any, selected: boolean) {
                                            // onItemSelect expects (key: Key, selected: boolean)
                                            onItemSelect(key, selected);
                                        },
                                        selectedRowKeys: selectedKeys,
                                        // No select-all checkbox in the column header.
                                        //
                                        // showSelectAll={false} on the Transfer hides ITS header, but the
                                        // inner Table draws its own, which landed right under the
                                        // «Αναζήτηση» box looking like a stray tick with nothing to do
                                        // with searching. It is also the wrong control here: it selects
                                        // whatever the current filter shows, so one click could mark
                                        // dozens of medicines a patient does not take.
                                        //
                                        // hideSelectAll, not columnTitle: antd's useSelection does
                                        // `if (!rowSelection?.columnTitle) return title;` - a null or ""
                                        // is falsy, so it would fall straight back to rendering the
                                        // checkbox. hideSelectAll suppresses it at the source.
                                        hideSelectAll: true,
                                    } as any;

                                    return (
                                        <Table
                                            rowSelection={rowSelection}
                                            columns={columns as any}
                                            dataSource={filteredItems}
                                            size="small"
                                            onRow={({ key }) => ({ onClick: () => onItemSelect(key, !selectedKeys.includes(key)) })}
                                            pagination={false}
                                            rowKey="key"
                                            // Scroll inside the pane rather than growing it: a long list
                                            // (38 comorbidities) would otherwise push the save button off
                                            // screen and make the two panes different heights. antd's
                                            // scroll.y keeps the column header pinned while the body
                                            // scrolls, so the header stays readable while picking.
                                            scroll={{ y: TRANSFER_PANE_BODY_HEIGHT }}
                                        />
                                    )
                                }}
                            </Transfer>
                        );
                    }

                    return <TableTransfer />;
                }
            case FieldTypeEnum.Integer:
                return withNotSet(
                    <InputNumber
                        style={{ width: '100%' }}
                        addonAfter={buildAddonAfter(hasUnits ? formField.units : undefined)}
                        placeholder={effectivePlaceholder ?? rangePlaceholder}
                        type="number"
                        disabled={notSetChecked}
                    />
                );
            case FieldTypeEnum.Double:
                return withNotSet(
                    <InputNumber
                        style={{ width: '100%' }}
                        disabled={notSetChecked || formField.extraStyle?.includes("Calculated")}
                        placeholder={effectivePlaceholder ?? rangePlaceholder}
                        addonAfter={buildAddonAfter(hasUnits ? <>
                            <span>{formField.units}</span>
                            {formField.extraStyle?.includes("Calculated") && <Button onClick={() => { buttonCalculateClick(formField); }}><CalculatorFilled title={"Αυτόματος υπολογισμός"}></CalculatorFilled>Αυτόματος υπολογισμός</Button>}</> : undefined)}
                        type="number" step={formField.interval ?? 0.1} />
                );
            case FieldTypeEnum.DateTime:
                {

                    // validate DateTimeFormat from extraStyle to a PickerMode accepted by antd DatePicker
                    const allowedPickers = ['date', 'week', 'month', 'quarter', 'year'] as const;
                    type AllowedPicker = (typeof allowedPickers)[number];
                    let pickerMode: AllowedPicker | undefined = undefined;
                    if (extraStyle?.DateTimeFormat && (allowedPickers as readonly string[]).includes(extraStyle.DateTimeFormat)) {
                        pickerMode = extraStyle.DateTimeFormat as AllowedPicker;
                    }

                    // showTime only applies to the default/date picker - passing it to
                    // week/month/quarter/year renders a time column antd cannot honour.
                    const isDateMode = pickerMode === undefined || pickerMode === 'date';

                    // Time is opt-in via ExtraStyle {"ShowTime":true}. Clinical dates
                    // (visit date, diagnosis date) are date-only, so a field must ask
                    // for a time component rather than get one by default - recording a
                    // spurious 00:00 would imply a precision that was never collected.
                    const showTime = isDateMode && extraStyle?.ShowTime === true;

                    // A DayOptional month field renders as a full day picker: the day
                    // is entered when it is known and left at the mid-month default
                    // when it is not. See isDayOptional.
                    //
                    // No extra checkbox for "day unknown". These rows already carry a
                    // not-set toggle and, on lab fields, a date-fill button; a third
                    // control would crowd the row for a distinction the investigator
                    // makes implicitly by either picking the real day or not. The
                    // default the picker opens on is the 15th, so leaving it alone
                    // records month precision exactly as before.
                    const dayOptional = isDayOptional(formField);
                    if (dayOptional) {
                        pickerMode = undefined;
                    }

                    // Almost no clinical observation in this CRF can happen after
                    // today: a visit, a diagnosis and a lab result have all already
                    // occurred by the time they are recorded. Future dates there are
                    // always typos, and they silently corrupt every duration formula
                    // that reads them (yearsBetween would return a negative age).
                    //
                    // The exception is a date that records something *scheduled* -
                    // a booked nephrology appointment - which opts in with
                    // {"AllowFuture":true}. Opt-in rather than opt-out so a new date
                    // field is restricted by default.
                    //
                    // endOf('day') so today itself stays selectable in every picker
                    // mode - month/year pickers compare against the start of the
                    // period, and a bare isAfter(now) would reject the current month.
                    const allowFuture = extraStyle?.AllowFuture === true;

                    // {"NotAfterField":9} caps this date at another field's value.
                    //
                    // Section E's follow-up start and T2DM diagnosis dates cannot be
                    // later than the visit being recorded - a diagnosis in the future
                    // relative to its own visit is a typo, and it makes the derived
                    // durations negative.
                    //
                    // Read live from the form rather than captured once, so moving the
                    // visit date immediately re-caps the fields that depend on it.
                    const notAfterFieldId = extraStyle?.NotAfterField;
                    const notAfterValue = notAfterFieldId != null
                        ? antdForm.getFieldValue(`field-${notAfterFieldId}`)
                        : null;
                    const notAfter = dayjs.isDayjs(notAfterValue)
                        ? notAfterValue.endOf('day')
                        : (notAfterValue ? dayjs(notAfterValue).endOf('day') : null);

                    const disabledDate = (current: any) => {
                        if (!current) return false;
                        if (!allowFuture && current.isAfter(dayjs().endOf('day'))) return true;
                        // A missing cap must not silently disable the whole calendar:
                        // if the visit date has not been entered yet, this field stays
                        // fully usable and is only bounded by "not in the future".
                        if (notAfter && notAfter.isValid() && current.isAfter(notAfter)) return true;
                        return false;
                    };

                    // placeholder is passed explicitly because antd supplies its own
                    // localized "Επιλέξτε ημερομηνία" when the prop is absent - so
                    // there is no other way to clear it while "not set" is ticked.
                    // undefined (not "") restores antd's default in the normal case.
                    return withNotSet(
                        <SafeDatePicker
                            style={{ width: '100%' }}
                            placeholder={notSetChecked ? "" : (dayOptional ? "ΕΕΕΕ-ΜΜ-ΗΗ (η ημέρα προαιρετικά)" : undefined)}
                            {...(showTime ? { showTime: true } : {})}
                            {...(pickerMode ? { picker: pickerMode } : {})}
                            {...(dayOptional ? {
                                // Opens on the 15th so an investigator who only knows
                                // the month lands on the protocol's mid-month
                                // convention by default, instead of on whichever day
                                // the calendar happened to highlight.
                                defaultPickerValue: dayjs().date(MONTH_PRECISION_DAY)
                            } : {})}
                            disabledDate={disabledDate}
                            disabled={notSetChecked}
                        />
                    );
                }
            case FieldTypeEnum.Percentage:
                if (formField.interval) {
                    let marks = {} as Record<string | number, ReactNode>;
                    for (let i = formField.min ?? 0; i <= (formField.max ?? 100); i += formField.interval ?? 1) {
                        marks[i.toString()] = i;
                    }

                    return <Slider
                        marks={marks}
                    />
                }

                return <Input addonAfter={hasUnits ? "%" : undefined} />;
            case FieldTypeEnum.Seperator:
                return <Divider />
            case FieldTypeEnum.Label:
                return <><p style={{ fontWeight: 600 }}>{formField.label}</p><Divider /></>
            default:
                return <></>;
        }
    }

    const getRules = (showMessage: boolean = true, suffix: number = 0) => {
        let rules = [] as Rule[];
        const notSetKey = `fieldmeta-${formField.id + suffix}-notset`;

        // Server-derived fields are never user-entered, so no rule may block a save.
        if (extraStyle?.DerivedCategories || formulaSpec) {
            return rules;
        }

        if (formField.fieldType != FieldTypeEnum.Label) {
            if (formField.allowNotSet) {
                // Applies whether or not the field is Required: with AllowNotSet the
                // user must either tick the checkbox or supply a value - never both empty.
                rules.push(({ getFieldValue }) => ({
                    validator(_, value) {
                        if (getFieldValue(notSetKey)) {
                            // ticked -> field is intentionally null, nothing to validate
                            return Promise.resolve();
                        }
                        if (isEmptyValue(value)) {
                            // Kept short so it fits on one line next to the checkbox -
                            // the checkbox itself is visible right there, so repeating
                            // its label in the message adds length without adding info.
                            return Promise.reject(!showMessage ? "" : `Υποχρεωτικό πεδίο ή επιλέξτε "${extraStyle?.AllowNotSetLabel ?? DEFAULT_NOT_SET_LABEL}"`);
                        }
                        return Promise.resolve();
                    }
                }));
            }
            else if (formField.required) {
                rules.push({ required: formField.required, message: !showMessage ? "" : `Το πεδίο "${formField.label}" είναι υποχρεωτικό ` });
            }
        }

        // Bound checks must not fire on a deliberately-null ("not set") field.
        const boundRule = (r: Rule): Rule => {
            if (!formField.allowNotSet) return r;
            return ({ getFieldValue }) => ({
                async validator(rule: any, value: any) {
                    if (getFieldValue(notSetKey) || isEmptyValue(value)) return;
                    const inner: any = r;
                    if (inner.max != null && typeof value === 'number' && value > inner.max) {
                        throw new Error(inner.message);
                    }
                    if (inner.min != null && typeof value === 'number' && value < inner.min) {
                        throw new Error(inner.message);
                    }
                },
                warningOnly: (r as any).warningOnly,
                validateTrigger: (r as any).validateTrigger,
            }) as any;
        };

        if (formField.max != null) {
            rules.push(boundRule({ type: 'number', max: formField.max, message: !showMessage ? "" : `Εισάγετε μια τιμη μικρότερη απο ${formField.max}` }));
        }

        if (formField.min != null) {
            rules.push(boundRule({ type: 'number', min: formField.min, message: !showMessage ? "" : `Εισάγετε μια τιμη μεγαλύτερη απο ${formField.min}` }));
        }

        if (formField.minAlert) {
            rules.push(boundRule({ type: 'number', validateTrigger: "onChange", min: formField.minAlert, warningOnly: true, message: !showMessage ? "" : `Πιθανή ακραία τιμή` }));
        }

        if (formField.maxAlert) {
            rules.push(boundRule({ type: 'number', validateTrigger: "onChange", max: formField.maxAlert, warningOnly: true, message: !showMessage ? "" : `Πιθανή ακραία τιμή` }));
        }

        // A hardcoded rule on field id 23 used to force "ABI not measured" here.
        // Removed at the client's request, and it was wrong besides: FormFields is
        // keyed on (FormId, Id), so ids repeat across sections and this fired on
        // whatever field happened to be number 23 in ANY form - including the new
        // Αιμοκάθαρση in Section G. Required-ness belongs in the field definition,
        // not in a per-id branch in the renderer.

        if (formField.id == 55) {
            rules.push(
                ({ getFieldValue }) => ({
                    validator(_, value) {
                        var chValue = getFieldValue('field-96');
                        var hasValue = value != null;
                        if (hasValue && chValue?.length > 0) {
                            return Promise.reject("Αν το πεδίο hCRP συμπληρωθεί, πρέπει να ΜΗΝ επιλέξετε ότι δεν μετρήθηκε");
                        }

                        if (!value && !getFieldValue('field-96')) {
                            return Promise.reject("Αν το πεδίο hCRP δεν συμπληρωθεί, πρέπει να επιλέξετε ότι δεν μετρήθηκε");
                        }

                        return Promise.resolve();
                    },
                }));
        }

        return rules;
    }

    const getLabel = () => {
        if (formField.fieldType == FieldTypeEnum.Label || formField.fieldType == FieldTypeEnum.Seperator) {
            return "";
        }

        return formField.label;
    }

    const getTooltip = () => {
        if (!formField.tooltip || formField.fieldType == FieldTypeEnum.Label || formField.fieldType == FieldTypeEnum.Seperator) {
            return null;
        }

        return formField.tooltip;
    }

    const getLabelWithInquiry = () => {
        const labelText = getLabel();
        if (formField.fieldType === FieldTypeEnum.Label || formField.fieldType === FieldTypeEnum.Seperator) {
            return "";
        }

        // Derived fields hold no user-entered value, so there is nothing to raise a
        // query about - the answer would be in the source fields instead.
        if (isDerived) {
            return <span>{labelText}</span>;
        }

        // Muted message icon, deliberately NOT a blue question mark: antd renders its
        // own blue "?" for the Form.Item tooltip, and two identical icons meaning
        // different things (help text vs. open a query) is a usability trap.
        return (
            <span className="field-label-with-inquiry">
                {labelText}
                <Tooltip title="Αποστολή ερώτησης">
                    <Button
                        className="field-inquiry-button"
                        size="small"
                        type="text"
                        icon={<MessageOutlined />}
                        style={{ marginLeft: 4 }}
                        // Out of the natural tab order: it is a secondary action that
                        // sits *before* the input in the DOM, so leaving it focusable
                        // would put an invisible stop in front of every field and
                        // double the tab presses needed to fill the form. Still
                        // reachable by mouse, and by keyboard via the browser's
                        // hover/focus-visible affordances.
                        tabIndex={-1}
                        onClick={(e) => { e.preventDefault(); setInquiryModalOpen(true); }}
                    />
                </Tooltip>
            </span>
        );
    };

    const getValueFromEvent = (values: any) => {
        if (formField.fieldType != FieldTypeEnum.CheckBox) {
            return values;
        }

        return values.map((id: number) => ({ id }));
    }
    const getValueProps = (ids: any) => {
        if (formField.fieldType != FieldTypeEnum.CheckBox) {
            return ids;
        }
    }

    const handleResolve = async () => {
        if (!pendingInquiry) return;
        const ok = await resolveInquiry(pendingInquiry.id);
        if (ok) {
            message.success("Η ερώτηση επιλύθηκε");
            onInquiryResolved?.(pendingInquiry.id);
        } else {
            message.error("Σφάλμα κατά την επίλυση της ερώτησης");
        }
    };

    // A mirror field is server-owned and never shown: it exists only so a formula on
    // this form can read a value entered in another section. It still needs a
    // Form.Item so the value lives in the store where useWatch can see it.
    if (isMirrorField(formField)) {
        return (
            <Form.Item name={`field-${formField.id}`} hidden noStyle>
                <Input type="hidden" />
            </Form.Item>
        );
    }

    return (<>
        {extraStyle?.FollowUp &&
            <Space.Compact style={{ width: "100%" }}>

                {renderWithNotSet(
                    <Form.Item
                        colon={false}
                        key={formField.id}
                        label={getLabel()}
                        name={`field-${formField.id}`}
                        rules={getRules(false)}
                        tooltip={getTooltip()}
                        hasFeedback
                    >
                        {getFieldComponent(false, "Baseline", 0)}
                    </Form.Item>, 0)}

                {renderWithNotSet(
                    <Form.Item
                        colon={false}
                        key={formField.id + "_1"}
                        name={`field-${formField.id + 1000}`}
                        rules={getRules(true, 1000)}
                        tooltip={getTooltip()}
                        hasFeedback
                    >
                        {getFieldComponent(true, "3μηνη παρακολούθηση", 1000)}
                    </Form.Item>, 1000)}
            </Space.Compact>
        }
        {/* A conditional field whose parent rules it out. Rather than vanishing -
            which leaves no trace that the question exists - it collapses to one
            muted line, so "not applicable" is distinguishable from "this eCRF
            never asked". Kept out of the tab order and holding no control, so it
            costs a line rather than a row. */}
        {!!!extraStyle?.FollowUp && !visible && !!extraStyle?.Conditional &&
            <div className="ecrf-conditional-hidden">
                <MinusOutlined className="ecrf-conditional-hidden-icon" />
                <span>{getLabel()}</span>
                <span className="ecrf-conditional-hidden-note">δεν εφαρμόζεται</span>
            </div>
        }
        {!!!extraStyle?.FollowUp && visible &&
            <div
                id={`field-inquiry-${formField.id}`}
                className={[
                    isHighlighted || pendingInquiry ? "field-inquiry-pending" : "",
                    // Indent a conditional child so its dependency on the field
                    // above is visible rather than inferred from the wording.
                    extraStyle?.Conditional ? "ecrf-conditional-child" : "",
                ].filter(Boolean).join(" ") || undefined}
            >


                {pairedField
                    ? (
                        // Value and its measurement date on one row, sharing the
                        // value's label. The date needs no label of its own - it is
                        // the only date on the row and its placeholder says so - and
                        // giving it one doubled the height of Section I to restate
                        // the line above it.
                        <div className="ecrf-paired-row">
                            <Form.Item
                                colon={false}
                                key={formField.id}
                                label={getLabelWithInquiry()}
                                name={`field-${formField.id}`}
                                rules={getRules()}
                                tooltip={getTooltip()}
                                hasFeedback
                                className="ecrf-paired-primary"
                            >
                                {getFieldComponent(true, undefined, 0)}
                            </Form.Item>
                            <div className="ecrf-paired-secondary">
                                {/* The partner is a full field - it keeps its own
                                    rules, not-set toggle and value binding - drawn
                                    without a label. Reusing FormFieldRender rather
                                    than a cut-down copy means the two can never
                                    drift apart in behaviour. */}
                                <FormFieldRender
                                    formField={pairedField}
                                    section={section}
                                    allFormValues={allFormValues}
                                    pendingInquiries={pendingInquiries}
                                    highlightedFieldId={highlightedFieldId}
                                    onInquiryResolved={onInquiryResolved}
                                    hideLabel
                                />
                            </div>
                        </div>
                    )
                    : renderWithNotSet(
                        <Form.Item
                            colon={false}
                            key={formField.id}
                            label={hideLabel ? undefined : getLabelWithInquiry()}
                            name={`field-${formField.id}`}
                            rules={getRules()}
                            tooltip={hideLabel ? undefined : getTooltip()}
                            hasFeedback
                            className={hideLabel ? "ecrf-nolabel" : undefined}
                        >
                            {getFieldComponent(true, undefined, 0)}
                        </Form.Item>, 0)}

                {pendingInquiry && (
                    <div className="inquiry-resolve-row">
                        <Tag color="gold" icon={<QuestionCircleOutlined />}>
                            Ερώτηση από {pendingInquiry.fromUserName}
                            {pendingInquiry.note ? `: ${pendingInquiry.note}` : ""}
                        </Tag>
                        <Checkbox onChange={(e) => { if (e.target.checked) handleResolve(); }}>
                            Επίλυση ερώτησης
                        </Checkbox>
                    </div>
                )}

                <InquiryModal
                    open={inquiryModalOpen}
                    onClose={() => setInquiryModalOpen(false)}
                    formField={formField}
                    formId={section.form?.id ?? 0}
                    sectionId={section.id}
                    ecrfId={section.ecrfId}
                    currentFieldValue={antdForm.getFieldValue(`field-${formField.id}`)?.toString()}
                />
            </div>
        }
    </>
    );
}
